import { Box, Divider, Modal, Typography } from "@mui/material";
import {
  useGetBreakdownProcessV2,
  useCreateChildPartProcessV2,
  useUpdateChildPartProcessV2,
  useDeleteChildPartProcessV2,
} from "@/hooks/breakdownHooks";
import CloseButton from "@/components/buttons/CloseButton";
import { InformationModal } from "@/styles/globalstyles";
import { useQueryHandler } from "@/hooks/useQueryHandler";
import EditableProcessList from "./EditableProcessList";
import { useGetChildPartProcesses } from "@/hooks/masterHook";
import { type Master } from "@/api/masterService";
import { useState } from "react";
import { ErrorAlert } from "@/utils/alert";
import { getAxiosErrorMessage } from "@/utils/healper";

interface ManageChildPartProcessModalProps {
  open: boolean;
  handleClose: () => void;
  supplierCode?: string;
  partId?: number;
  version: "v1" | "v2";
  canEdit: boolean;
}

const ManageChildPartProcessModal = ({
  open,
  handleClose,
  partId,
  canEdit,
}: ManageChildPartProcessModalProps) => {
  const [disabled, setDisabled] = useState(false);
  const {
    data: breakdownProcessV2,
    isLoading: isLoadingBreakdownProcessV2,
    isError: isErrorBreakdownProcessV2,
  } = useGetBreakdownProcessV2(partId || 0);

  const {
    data: ChildPartProcesses,
    isLoading: isLoadingChildPartProcesses,
    isError: isErrorChildPartProcesses,
  } = useGetChildPartProcesses(
    { skip: 0, limit: 1000, active_only: true },
    true,
  );

  const {
    mutate: createChildPartProcessV2,
    isPending: isCreatingChildPartProcessV2,
  } = useCreateChildPartProcessV2();

  const {
    mutate: updateChildPartProcessV2,
    isPending: isUpdatingChildPartProcessV2,
  } = useUpdateChildPartProcessV2();

  const {
    mutate: deleteChildPartProcessV2,
    isPending: isDeletingChildPartProcessV2,
  } = useDeleteChildPartProcessV2();

  useQueryHandler({
    isFetching:
      isLoadingBreakdownProcessV2 ||
      isLoadingChildPartProcesses ||
      isCreatingChildPartProcessV2 ||
      isUpdatingChildPartProcessV2 ||
      isDeletingChildPartProcessV2,
    isError: isErrorBreakdownProcessV2 || isErrorChildPartProcesses,
  });

  const onClose = () => {
    handleClose();
    setDisabled(false);
  };

  const handleAddChildPartProcess = async (process: {
    name: string;
    master_process_id: number;
  }) => {
    createChildPartProcessV2(
      {
        partId: partId || 0,
        params: {
          cp_processes: [
            { ms_child_part_process_id: process.master_process_id },
          ],
        },
      },
      {
        onError: (error) => {
          console.error("Error creating child part process:", error);
          ErrorAlert("Error", getAxiosErrorMessage(error));
        },
      },
    );
  };

  const handleEditChildPartProcess = async (process: {
    id: number;
    name: string;
    master_process_id: number;
  }) => {
    updateChildPartProcessV2(
      {
        partId: partId || 0,
        params: {
          cp_processes: [
            {
              part_cp_process_id: process.id,
              ms_child_part_process_id: process.master_process_id,
            },
          ],
        },
      },
      {
        onError: (error) => {
          console.error("Error updating child part process:", error);
          ErrorAlert("Error", getAxiosErrorMessage(error));
        },
      },
    );
  };

  const handleDeleteChildPartProcess = async (processId: number) => {
    deleteChildPartProcessV2(
      {
        partId: partId || 0,
        params: {
          cp_processes: [{ part_cp_process_id: processId }],
        },
      },
      {
        onError: (error) => {
          console.error("Error deleting child part process:", error);
          ErrorAlert("Error", getAxiosErrorMessage(error));
        },
      },
    );
  };

  return (
    <Modal
      open={open}
      onClose={onClose}
      aria-labelledby="modal-modal-title"
      aria-describedby="modal-modal-description"
    >
      <Box
        sx={{
          ...InformationModal,
          minHeight: "400px",
          maxHeight: "90vh",
          width: "70vw",
        }}
      >
        <CloseButton onClick={onClose} />
        <Typography variant="h2">{"จัดการ Child Part Process"}</Typography>
        <Divider sx={{ my: 1 }} />
        <Box display="grid" gridTemplateColumns="1fr" gap={2} p={1} pt={2}>
          <EditableProcessList
            title="Child Part Process"
            data={
              breakdownProcessV2?.cp_process?.map((process) => ({
                id: process.id,
                name: process.name,
                master_process_id: process.master_process_id || 0,
              })) || []
            }
            options={
              ChildPartProcesses?.map((process: Master) => ({
                value: process.id,
                label: process.name,
              })) || []
            }
            handleAddProcess={async (process) => {
              handleAddChildPartProcess(process);
            }}
            handleEditProcess={async (process) => {
              handleEditChildPartProcess(process);
            }}
            handleDeleteProcess={async (processId) => {
              handleDeleteChildPartProcess(processId);
            }}
            disabled={disabled || !canEdit}
            setDisabled={setDisabled}
          />
        </Box>
      </Box>
    </Modal>
  );
};

export default ManageChildPartProcessModal;
